import {IDatePropertyTypeFullValue, IDatePropertyValue} from './types/IDatePropertyTypeInterfaces'

export const DefaultDateFormat = 'dd-MM-yyyy'

export const createEmptyDatePart = (): IDatePropertyValue => ({
  date: '',
  time: '',
})

export const normalizeDatePartTime = (value: unknown): string => {
  if(typeof value !== 'string' || !value.trim()) {
    return ''
  }
  const match = value.trim().match(/^(\d{1,2}):(\d{1,2})(?::(\d{1,2}))?$/)
  if(!match) {
    return ''
  }
  const [, hoursRaw, minutesRaw, secondsRaw] = match
  const hours = String(Number(hoursRaw)).padStart(2, '0')
  const minutes = String(Number(minutesRaw)).padStart(2, '0')

  if(secondsRaw === undefined) {
    return `${hours}:${minutes}` === '00:00' ? '' : `${hours}:${minutes}`
  }

  const seconds = String(Number(secondsRaw)).padStart(2, '0')

  if(seconds === '00') {
    return `${hours}:${minutes}` === '00:00' ? '' : `${hours}:${minutes}`
  }

  return `${hours}:${minutes}:${seconds}`
}

export const normalizeDatePartDate = (value: unknown): string => {
  if(typeof value !== 'string' || !value.trim()) {
    return ''
  }
  const raw = value.trim()
  const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if(isoMatch) {
    const [, year, month, day] = isoMatch
    return `${year}-${month}-${day}`
  }
  const dayFirstMatch = raw.match(/^(\d{2})-(\d{2})-(\d{4})$/)
  if(dayFirstMatch) {
    const [, day, month, year] = dayFirstMatch
    return `${year}-${month}-${day}`
  }
  return ''
}

export const createDateValue = (date: string, time?: string): IDatePropertyTypeFullValue => ({
  from: {
    date,
    time: normalizeDatePartTime(time),
  },
  to: createEmptyDatePart(),
})

const dateToDateValue = (value: Date): IDatePropertyTypeFullValue => {
  const hours = value.getHours()
  const minutes = value.getMinutes()
  const seconds = value.getSeconds()
  const time = seconds === 0 ? `${hours}:${minutes}` : `${hours}:${minutes}:${seconds}`
  return createDateValue(
    `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}`,
    time,
  )
}

const parseDateStringValue = (value: string): IDatePropertyTypeFullValue | null => {
  const trimmed = value.trim()
  const dayFirstMatch = trimmed.match(
    /^(\d{2})-(\d{2})-(\d{4})(?:[T\s](\d{1,2}):(\d{1,2})(?::(\d{1,2}))?)?$/,
  )
  if(dayFirstMatch) {
    const [, day, month, year, hoursRaw, minutesRaw, secondsRaw] = dayFirstMatch
    const time = hoursRaw !== undefined
      ? (secondsRaw === undefined ? `${hoursRaw}:${minutesRaw}` : `${hoursRaw}:${minutesRaw}:${secondsRaw}`)
      : ''
    return createDateValue(`${year}-${month}-${day}`, time)
  }

  const isoMatch = trimmed.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{1,2}):(\d{1,2})(?::(\d{1,2}))?)?/,
  )
  if(isoMatch) {
    const [, year, month, day, hoursRaw, minutesRaw, secondsRaw] = isoMatch
    const time = hoursRaw !== undefined
      ? (secondsRaw === undefined ? `${hoursRaw}:${minutesRaw}` : `${hoursRaw}:${minutesRaw}:${secondsRaw}`)
      : ''
    return createDateValue(`${year}-${month}-${day}`, time)
  }

  const normalizedDate = normalizeDatePartDate(trimmed)
  if(normalizedDate) {
    return createDateValue(normalizedDate)
  }

  const parsedDate = new Date(trimmed)
  if(isNaN(parsedDate.getTime())) {
    return null
  }
  return dateToDateValue(parsedDate)
}

const readDatePart = (value: unknown): IDatePropertyValue | null => {
  if(!value || typeof value !== 'object') {
    return null
  }
  const {date, time} = value as {date?: unknown, time?: unknown}
  if(typeof date !== 'string' && typeof time !== 'string') {
    return null
  }
  return {
    date: typeof date === 'string' ? date : '',
    time: typeof time === 'string' ? time : '',
  }
}

export const isDateRangeValue = (value: unknown): boolean => {
  return !!value && typeof value === 'object' && 'from' in value && 'to' in value
}

export const isDateValue = (value: unknown): boolean => {
  if(!value || typeof value !== 'object') {
    return false
  }
  if('from' in value) {
    return !!readDatePart((value as {from?: unknown}).from)?.date
  }
  return !!readDatePart(value)?.date
}

export const dateValueToText = (value: unknown): string => {
  if(!value || typeof value !== 'object') {
    return ''
  }
  const hasRange = 'from' in value
  const from = hasRange ? readDatePart((value as {from?: unknown}).from) : readDatePart(value)
  const to = hasRange ? readDatePart((value as {to?: unknown}).to) : null

  let text = ''
  if(from?.date) {
    text += from.date
  }
  if(from?.time) {
    text += `${text ? ' ' : ''}${from.time}`
  }
  if(to?.date) {
    text += `${text ? ' - ' : ''}${to.date}`
  }
  if(to?.time) {
    text += `${text ? ' ' : ''}${to.time}`
  }
  return text
}

export const toDateValue = (value: unknown): IDatePropertyTypeFullValue | null => {
  if(value === null || value === undefined) {
    return null
  }

  if(isDateRangeValue(value)) {
    const rangeValue = value as {from?: unknown, to?: unknown}
    const from = readDatePart(rangeValue.from)
    const to = readDatePart(rangeValue.to)
    const fromDate = normalizeDatePartDate(from?.date)
    if(!fromDate) {
      return null
    }
    const hasTo = !!(to && (to.date || to.time))
    return {
      from: {
        date: fromDate,
        time: normalizeDatePartTime(from?.time),
      },
      to: hasTo ? {
        date: normalizeDatePartDate(to.date),
        time: normalizeDatePartTime(to.time),
      } : createEmptyDatePart(),
    }
  }

  if(typeof value === 'object' && !(value instanceof Date)) {
    const part = readDatePart(value)
    const normalizedDate = normalizeDatePartDate(part?.date)
    if(!normalizedDate) {
      return null
    }
    return {
      from: {
        date: normalizedDate,
        time: normalizeDatePartTime(part.time),
      },
      to: createEmptyDatePart(),
    }
  }

  if(value instanceof Date) {
    return isNaN(value.getTime()) ? null : dateToDateValue(value)
  }

  if(typeof value === 'number') {
    const parsedDate = new Date(value)
    return isNaN(parsedDate.getTime()) ? null : dateToDateValue(parsedDate)
  }

  if(typeof value === 'string') {
    return parseDateStringValue(value)
  }

  return null
}
